import loading from '../assets/loading.svg';
import ProductQuantityIncrement from './ProductQuantityIncrement';
import { formatPrice } from '../utils/numberFormat';
import { CartPrescriptionDetail } from './CartPrescriptionDetails';
import {
  CartOrderSummaryProps,
  PrescriptionStatus,
} from '../interface/interface';
import {
  creditCardProducts,
  Messages,
  surchargeLineItemTitles,
} from '../constant/constant';
import InfoBox from './InfoBox';
import { useContext } from 'react';
import { UserContext } from '../context/userContext';

const CartOrderSummary = (props: CartOrderSummaryProps) => {
  const { cartData, shippingLoading, addProductToCart, removeProductFromCart } =
    props;

  const { isMobile } = useContext(UserContext);

  const isImprosonate = localStorage.getItem('impersonateUser');

  const orderedItems = cartData?.line_items?.filter(
    (item) =>
      !surchargeLineItemTitles.some((title) => item.title.startsWith(title)),
  );

  return (
    <>
      <div className="cart-items">
        {orderedItems?.map((item, index: number) => (
          <div
            className="cart-item border border-secondary-pink-50 rounded shadow-sm mb-3"
            key={index}
          >
            {!isMobile && item.requiresPrescription && (
              <div className="prescription-details bg-secondary-pink-50 px-5 py-4">
                <h3 className="text-base md:text-xl text-primary-blue-500 font-bold mb-4">
                  Prescription Details{' '}
                </h3>
                {item.prescription.status !== PrescriptionStatus.Active && (
                  <InfoBox content={Messages.NON_ACTIVE_PRESCRIPTION_IN_CART} />
                )}
                {item.prelimChecksError && (
                  <InfoBox content={item.prelimChecksError} />
                )}
                <div className="flex flex-col md-large:flex-row md-large:justify-between -mx-5">
                  {isImprosonate ? (
                    <CartPrescriptionDetail
                      label="Token"
                      value={
                        item.prescription?.token
                          ? item.prescription?.token
                          : '-'
                      }
                      className="cart-presc-small"
                    />
                  ) : (
                    process.env.VITE_ERX === 'ENABLE' && (
                      <CartPrescriptionDetail
                        label="Token"
                        value={
                          item.prescription?.token
                            ? item.prescription?.token
                            : '-'
                        }
                        className="cart-presc-small"
                      />
                    )
                  )}
                  <CartPrescriptionDetail
                    label="Medication Name"
                    value={item.prescription?.drugName ?? '-'}
                  />
                  <CartPrescriptionDetail
                    label="Brand Name"
                    value={item.prescription?.brandName ?? '-'}
                  />
                  <CartPrescriptionDetail
                    label="Medication Form"
                    value={item.prescription?.medicationForm ?? '-'}
                    className="cart-presc-small"
                  />
                  <CartPrescriptionDetail
                    label="Medication Strength"
                    value={item.prescription?.medicationStrength ?? '-'}
                    className="cart-presc-small"
                  />
                </div>
              </div>
            )}
            <div className="product-details bg-white border-t border-secondary-pink-50 px-5 py-4">
              <div className="flex flex-col md:flex-row mb-4 md:items-center">
                <h3 className="text-base md:text-xl text-secondary-pink-700 font-bold  mr-4 items-center">
                  Matched Product Details
                </h3>
                <p className="p-2 uppercase text-xxs font-semibold text-white bg-secondary-cornflowerblue-600 rounded-full w-fit mt-3 md:mt-0">
                  {item.requiresPrescription
                    ? 'Matched Product'
                    : 'Non-Prescription Product'}
                </p>
              </div>
              <div className="hidden md-large:block overflow-x-auto">
                <table className="cart-table w-full">
                  <thead>
                    <tr>
                      <th className="pr-4 text-left text-xs text-secondary-pink-700 font-semibold pl-0">
                        Product Image
                      </th>
                      <th className="pr-4 text-left text-xs text-secondary-pink-700 font-semibold">
                        Product Name
                      </th>
                      {/* <th className="pr-4 text-left text-xs text-secondary-pink-700 font-semibold">
                          Product Details
                        </th> */}
                      <th className="pr-4 text-left text-xs text-secondary-pink-700 font-semibold">
                        Order Qty
                      </th>
                      <th className="pr-4 text-left text-xs text-secondary-pink-700 font-semibold">
                        Unit Price
                      </th>
                      <th className="pr-4 text-left text-xs text-secondary-pink-700 font-semibold">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <img
                          className="w-28"
                          src={
                            item?.imageUrl !== ''
                              ? item.imageUrl
                              : '/default-image.png'
                          }
                        />
                      </td>
                      <td className="pr-4 text-left text-sm text-primary-blue-500 font-regular w-[25%]">
                        {item.title}
                      </td>
                      {/* <td className="pr-4 text-left text-sm text-primary-blue-500 font-regular w-[35%]">
                          <RenderProductDescription
                            description={item?.description}
                          />
                        </td> */}
                      <td className="pr-4">
                        <div className="flex">
                          <ProductQuantityIncrement
                            productCode={item.product_id}
                            addProductToCart={(id: string) =>
                              addProductToCart(id, item.substituteForId)
                            }
                            productOutOfStock={item.outOfStock}
                            removeProductFromCart={removeProductFromCart}
                            orderedQuantity={item.quantity}
                            minimalist
                            isProductTile={true}
                          />
                        </div>
                      </td>
                      <td className="pr-4 text-left text-sm text-primary-blue-500 font-regular">
                        ${formatPrice(item?.price)}
                      </td>
                      <td className="pr-4 text-left text-sm text-primary-blue-500 font-regular">
                        ${formatPrice(item?.line_price)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="block md-large:hidden">
                <div className="rounded">
                  <div className="flex justify-between">
                    <img
                      className="w-24 h-24"
                      src={
                        item?.imageUrl !== ''
                          ? item.imageUrl
                          : '/default-image.png'
                      }
                    />
                    <div className="flex flex-col justify-center">
                      <p className="text-primary-blue-500 text-sm font-semibold">
                        {item.title}
                      </p>
                      <div className="flex mt-3 items-center">
                        <p className="text-sm mr-2">Qty:</p>
                        <div>
                          <ProductQuantityIncrement
                            productCode={item.product_id}
                            addProductToCart={(id: string) =>
                              addProductToCart(id, item.substituteForId)
                            }
                            productOutOfStock={item.outOfStock}
                            removeProductFromCart={removeProductFromCart}
                            orderedQuantity={item.quantity}
                            minimalist
                            isProductTile={true}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <ul>
                    <li className="rounded-md bg-secondary-cornflowerblue-50 p-4 flex justify-between items-center mb-2 mt-2">
                      <p className="text-sm text-primary-blue-500 font-semibold">
                        Unit Price
                      </p>
                      <p className="text-sm text-primary-blue-500 font-regular flex items-center">
                        ${formatPrice(item?.price)}
                      </p>
                    </li>
                    <li className="rounded-md bg-secondary-cornflowerblue-50 p-4 flex justify-between items-center mb-2">
                      <p className="text-sm text-primary-blue-500 font-semibold">
                        Amount
                      </p>
                      <p className="text-sm text-primary-blue-500 font-regular flex items-center">
                        ${formatPrice(item?.line_price)}
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-secondary-cornflowerblue-50 mt-5 justify-end p-4 rounded w-full flex flex-col items-end">
        {Number(
          cartData.line_items.find((item) =>
            creditCardProducts.some((name) => item.title.startsWith(name)),
          )?.line_price,
        ) > 0 && (
          <div className="flex items-center mb-2">
            <label className="mr-3 text-sm">Dispensing & Fulfilment Fee:</label>
            <p className="">
              {cartData?.line_items?.find((item) =>
                creditCardProducts.some((name) => item.title.startsWith(name)),
              )?.line_price
                ? `$${formatPrice(
                    cartData?.line_items?.find((item) =>
                      creditCardProducts.some((name) =>
                        item.title.startsWith(name),
                      ),
                    )?.line_price,
                  )}`
                : ''}
            </p>
          </div>
        )}
        <div className="flex items-center mb-2">
          <label className="mr-3 text-sm">Sub Total:</label>
          <p className="">
            {formatPrice(cartData?.subtotal_price)
              ? `$${formatPrice(cartData?.subtotal_price)}`
              : ''}
          </p>
        </div>
        {(!!formatPrice(cartData?.shipping_rate?.price) || shippingLoading) && (
          <div className="flex items-center mb-2">
            <div className="col-span-9 xl:col-span-10 text-end mr-3">
              {'Shipping Charge:'}
            </div>
            {shippingLoading ? (
              <div className="col-span-2 xl:col-span-1 text-end p-1">
                <img
                  height={'20px'}
                  width={'20px'}
                  src={loading}
                  alt="Loading"
                />
              </div>
            ) : (
              <div className="col-span-2 xl:col-span-1 text-end">
                {`$${formatPrice(cartData?.shipping_rate?.price)}`}
              </div>
            )}
          </div>
        )}
        {(!!cartData?.shipping_rate?.price || shippingLoading) && (
          <div className="flex items-center">
            <div className="col-span-9 xl:col-span-10 text-end mr-3 text-primary-blue-500 font-semibold">
              {'Total Price:'}
            </div>
            {shippingLoading ? (
              <div className="col-span-2 xl:col-span-1 text-end p-1">
                <img
                  width={'20px'}
                  height={'20px'}
                  src={loading}
                  alt="Loading"
                />
              </div>
            ) : (
              <div className="col-span-2 xl:col-span-1 text-end text-primary-blue-500 font-semibold">
                {`$${formatPrice(cartData?.total_price)}`}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default CartOrderSummary;
